import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  BarChart3, 
  Ticket, 
  MessageSquare, 
  Bell, 
  LogOut,
  Settings,
  Menu,
  X,
  Shield,
  Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const AdminLayout: React.FC = () => {
  const { profile, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/admin', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/tickets', label: 'Ticket Queue', icon: Ticket },
    { path: '/admin/messages', label: 'All Messages', icon: MessageSquare },
    { path: '/admin/reviews', label: 'Client Reviews', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-72 bg-[#0A0A0A] border-r border-white/5 p-6">
        <Link to="/" className="flex items-center gap-3 mb-10 px-2 group">
          <div className="relative w-20 h-20 rounded-full p-0.5 bg-[#261006] border border-[#caa062]/80 shadow-[0_8px_24px_rgba(0,0,0,0.9),inset_0_1.5px_2px_rgba(255,255,255,0.25)] shadow-md shrink-0 group-hover:scale-105 transition-transform">
            <img 
              src="/brand_logo.png?v=4" 
              alt="The Brown Pelican Man Man" 
              referrerPolicy="no-referrer"
              className="w-full h-full rounded-full object-contain" 
            />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-sm tracking-tight text-white group-hover:text-[#ffe0a3] transition-colors leading-tight">
              The Brown Pelican Man
            </span>
            <span className="text-[9px] font-mono text-[#d8aa68] uppercase tracking-wider">Admin Console</span>
          </div>
        </Link>

        <nav className="flex-1 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all group",
                location.pathname === item.path 
                  ? "bg-purple-500/10 text-purple-500 border border-purple-500/20" 
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              )}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-6 border-t border-white/5 space-y-4">
          <Link 
            to="/" 
            className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
          >
            <MessageSquare size={20} />
            <span className="font-medium">Client View</span>
          </Link>
          <div className="flex items-center gap-3 px-2">
            <img 
              src={profile?.photoURL} 
              alt={profile?.displayName} 
              className="w-20 h-20 rounded-full border border-white/10"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{profile?.displayName}</p>
              <p className="text-xs text-purple-500 uppercase tracking-widest font-bold">{profile?.role}</p>
            </div>
          </div>
          <button 
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-400/5 rounded-xl transition-all"
          >
            <LogOut size={20} />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-20 bg-[#0A0A0A]/50 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 md:px-10 sticky top-0 z-40">
          <div className="flex items-center gap-3 md:hidden">
            <button onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={24} />
            </button>
            <div className="flex items-center gap-2">
              <img 
                src="/brand_logo.png?v=4" 
                alt="Logo" 
                referrerPolicy="no-referrer"
                className="w-7 h-7 rounded-full object-contain" 
              />
              <span className="font-bold text-sm tracking-tight">The Brown Pelican Man</span>
            </div>
          </div>

          <div className="hidden md:block">
            <h2 className="text-lg font-bold">
              {navItems.find(i => i.path === location.pathname)?.label || 'Admin Dashboard'}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <button className="p-2 text-gray-400 hover:text-white relative">
              <Bell size={20} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-purple-500 rounded-full border-2 border-[#0A0A0A]"></span>
            </button>
            <div className="w-px h-6 bg-white/10 mx-2"></div>
            <button className="p-2 text-gray-400 hover:text-white">
              <Settings size={20} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] md:hidden"
            />
            <motion.aside 
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              className="fixed inset-y-0 left-0 w-72 bg-[#0A0A0A] z-[101] p-6 flex flex-col md:hidden"
            >
              <div className="flex items-center justify-between mb-10">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3">
                  <div className="relative w-32 h-32 rounded-full p-0.5 bg-[#261006] border border-[#caa062]/80 shadow-[0_8px_24px_rgba(0,0,0,0.9),inset_0_1.5px_2px_rgba(255,255,255,0.25)] shadow-sm shrink-0">
                    <img 
                      src="/brand_logo.png?v=4" 
                      alt="The Brown Pelican Man Man" 
                      referrerPolicy="no-referrer"
                      className="w-full h-full rounded-full object-contain" 
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-sm tracking-tight text-white leading-tight">
                      The Brown Pelican Man
                    </span>
                    <span className="text-[9px] font-mono text-[#d8aa68] uppercase tracking-wider">Admin Console</span>
                  </div>
                </Link>
                <button onClick={() => setIsMobileMenuOpen(false)}>
                  <X size={24} />
                </button>
              </div>

              <nav className="flex-1 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                      location.pathname === item.path 
                        ? "bg-purple-500/10 text-purple-500 border border-purple-500/20" 
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    )}
                  >
                    <item.icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </nav>

              <div className="mt-auto pt-6 border-t border-white/5 space-y-4">
                <button 
                  onClick={logout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-400/5 rounded-xl transition-all"
                >
                  <LogOut size={20} />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
